import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  createEmptyWorkflow,
  createWorkflowNode,
  createNodeConnection,
  validateConnection,
  snapToGrid,
} from '../../utils/sandbox/sandboxUtils';
import {
  createWorkflow,
  updateWorkflow as updateWorkflowService,
  getWorkflow,
  deleteWorkflow,
} from '../../API/services/workflowsService';
import { TRIGGER_TEMPLATES } from '../../constants/alkymation';
import { setWorkflowTriggerScreening } from '../../redux/actions/screeningActions';

export const useSandbox = () => {
  const dispatch = useDispatch();
  const { user, currentWorkspace } = useSelector((state) => state.auth);
  const [workflow, setWorkflow] = useState(createEmptyWorkflow());
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [connectionStart, setConnectionStart] = useState(null);
  const [tempConnection, setTempConnection] = useState(null);

  const updateWorkflowState = useCallback((newWorkflow) => {
    setWorkflow(newWorkflow);
  }, []);

  const addNode = useCallback((template, position) => {
    const snappedPosition = snapToGrid(position);
    const newNode = createWorkflowNode(template, snappedPosition);

    setWorkflow((prev) => ({
      ...prev,
      nodes: [...prev.nodes, newNode],
      updatedAt: new Date(),
    }));

    return newNode.id;
  }, []);

  const deactivateWorkflow = useCallback(async () => {
    if (workflow?._id && workflow?.active) {
      try {
        await updateWorkflowService(workflow._id, { ...workflow, active: false });
        setWorkflow((prev) => ({
          ...prev,
          active: false,
          updatedAt: new Date(),
        }));
        return true;
      } catch (error) {
        console.error('Error deactivating workflow:', error);
        return false;
      }
    }
    return false;
  }, [workflow]);

  const removeNode = useCallback(
    async (nodeId) => {
      const nodeToRemove = workflow.nodes.find((node) => node.id === nodeId);
      const isTriggerNode =
        nodeToRemove?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE ||
        nodeToRemove?.template?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE;

      if (isTriggerNode) {
        dispatch(setWorkflowTriggerScreening(null));
      }

      // Deactivate workflow if it's active and saved
      await deactivateWorkflow();

      setWorkflow((prev) => ({
        ...prev,
        nodes: prev.nodes.filter((node) => node.id !== nodeId),
        connections: prev.connections.filter(
          (conn) => conn.sourceNodeId !== nodeId && conn.targetNodeId !== nodeId
        ),
        updatedAt: new Date(),
      }));

      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
      }
    },
    [selectedNodeId, workflow.nodes, dispatch, deactivateWorkflow]
  );

  const updateNodePosition = useCallback((nodeId, position) => {
    const snappedPosition = snapToGrid(position);

    setWorkflow((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) =>
        node.id === nodeId ? { ...node, position: snappedPosition } : node
      ),
      updatedAt: new Date(),
    }));
  }, []);

  const updateNodeParameters = useCallback((nodeId, parameters) => {
    setWorkflow((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => (node.id === nodeId ? { ...node, parameters } : node)),
      updatedAt: new Date(),
    }));
  }, []);

  const addConnection = useCallback(
    (connectionData) => {
      const sourceNode = workflow.nodes.find((n) => n.id === connectionData.sourceNodeId);
      const targetNode = workflow.nodes.find((n) => n.id === connectionData.targetNodeId);

      const validation = validateConnection(
        sourceNode,
        connectionData.sourceOutputId,
        targetNode,
        connectionData.targetInputId,
        workflow.connections
      );

      if (!validation.valid) {
        throw new Error(validation.message);
      }

      const newConnection = createNodeConnection(
        connectionData.sourceNodeId,
        connectionData.sourceOutputId,
        connectionData.targetNodeId,
        connectionData.targetInputId
      );

      setWorkflow((prev) => ({
        ...prev,
        connections: [...prev.connections, newConnection],
        updatedAt: new Date(),
      }));

      return newConnection.id;
    },
    [workflow.nodes, workflow.connections]
  );

  const removeConnection = useCallback(
    async (connectionId) => {
      // Deactivate workflow if it's active and saved
      await deactivateWorkflow();

      setWorkflow((prev) => ({
        ...prev,
        connections: prev.connections.filter((conn) => conn.id !== connectionId),
        updatedAt: new Date(),
      }));
    },
    [deactivateWorkflow]
  );

  const saveWorkflow = useCallback(
    async (name, description = '') => {
      try {
        const triggerNode = workflow.nodes.find(
          (node) => node.template?.category === 'WORKFLOW_NODE_CATEGORY_TRIGGER'
        );

        if (!triggerNode) {
          const error = new Error('WORKFLOW_REQUIRES_TRIGGER');
          error.code = 'WORKFLOW_REQUIRES_TRIGGER';
          throw error;
        }

        const triggerParameters = {};
        if (triggerNode.parameters) {
          Object.assign(triggerParameters, triggerNode.parameters);
        }

        const workflowData = {
          name,
          description,
          nodes: workflow.nodes.map((node) => ({
            ...node,
            templateId: node.template?.templateId || node.templateId,
            template: node.template
              ? {
                  ...node.template,
                  n8nWorkflow: node.template.n8nWorkflow || null,
                }
              : undefined,
          })),
          connections: workflow.connections,
          workspaceId: currentWorkspace,
          userId: user.id,
          templateTriggerId: triggerNode.template?.templateId || triggerNode.templateId,
          triggerParameters,
        };

        let savedWorkflow;
        const { mongoId } = workflow;

        if (mongoId) {
          savedWorkflow = await updateWorkflowService(mongoId, workflowData);
        } else {
          savedWorkflow = await createWorkflow(workflowData);
        }

        const newMongoId = savedWorkflow.id || savedWorkflow._id?.$oid || savedWorkflow._id;
        const updatedWorkflow = {
          ...workflow,
          ...savedWorkflow,
          mongoId: newMongoId,
          name,
          description,
          updatedAt: new Date(),
        };

        setWorkflow(updatedWorkflow);
        return updatedWorkflow;
      } catch (error) {
        console.error('Error saving workflow:', error);
        throw error;
      }
    },
    [workflow, currentWorkspace, user]
  );

  const loadWorkflow = useCallback(
    async (workflowId) => {
      try {
        dispatch(setWorkflowTriggerScreening(null));

        const savedWorkflow = await getWorkflow(workflowId);
        const mongoId = savedWorkflow.id || savedWorkflow._id?.$oid || savedWorkflow._id;

        const loadedWorkflow = {
          ...savedWorkflow,
          mongoId,
          id: `workflow_${Date.now()}`,
        };

        setWorkflow(loadedWorkflow);
        setSelectedNodeId(null);
        setIsConfigModalOpen(false);

        const triggerNode = loadedWorkflow.nodes?.find(
          (node) =>
            node.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE ||
            node.template?.templateId === TRIGGER_TEMPLATES.WEBHOOK_SCREENING_COMPLETE
        );

        if (triggerNode?.parameters?.screeningId) {
          dispatch(setWorkflowTriggerScreening(triggerNode.parameters.screeningId));
        }

        return loadedWorkflow;
      } catch (error) {
        console.error('Error loading workflow:', error);
        throw new Error('Workflow no encontrado');
      }
    },
    [dispatch]
  );

  const createNewWorkflow = useCallback(
    (name = 'Nuevo Flujo') => {
      dispatch(setWorkflowTriggerScreening(null));

      const newWorkflow = createEmptyWorkflow(name);
      setWorkflow(newWorkflow);
      setSelectedNodeId(null);
      setIsConfigModalOpen(false);
      return newWorkflow;
    },
    [dispatch]
  );

  const deleteWorkflowById = useCallback(async (workflowId) => {
    try {
      await deleteWorkflow(workflowId);
      return true;
    } catch (error) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  }, []);

  const loadWorkflowFromTemplate = useCallback(
    (template) => {
      try {
        dispatch(setWorkflowTriggerScreening(null));

        const nodeIdMap = {};

        const newNodes = template.nodes.map((node) => {
          const newNodeId = `node_${Math.random().toString(36).substr(2, 9)}`;
          nodeIdMap[node.id] = newNodeId;

          return {
            ...node,
            id: newNodeId,
            parameters: {},
          };
        });

        const newConnections = template.connections.map((conn) => ({
          ...conn,
          id: `connection_${Math.random().toString(36).substr(2, 9)}`,
          sourceNodeId: nodeIdMap[conn.sourceNodeId] || conn.sourceNodeId,
          targetNodeId: nodeIdMap[conn.targetNodeId] || conn.targetNodeId,
        }));

        const newWorkflow = {
          id: `workflow_${Date.now()}`,
          name: template.name,
          description: template.description || '',
          nodes: newNodes,
          connections: newConnections,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        setWorkflow(newWorkflow);
        setSelectedNodeId(null);
        setIsConfigModalOpen(false);

        return newWorkflow;
      } catch (error) {
        console.error('Error loading template:', error);
        throw error;
      }
    },
    [dispatch]
  );

  const duplicateNode = useCallback(
    (nodeId) => {
      const nodeToDuplicate = workflow.nodes.find((n) => n.id === nodeId);
      if (!nodeToDuplicate) return;

      const newPosition = {
        x: nodeToDuplicate.position.x + 50,
        y: nodeToDuplicate.position.y + 50,
      };

      const duplicatedNode = createWorkflowNode(nodeToDuplicate.template, newPosition);
      duplicatedNode.parameters = { ...nodeToDuplicate.parameters };
      duplicatedNode.name = `${nodeToDuplicate.name} (Copia)`;

      setWorkflow((prev) => ({
        ...prev,
        nodes: [...prev.nodes, duplicatedNode],
        updatedAt: new Date(),
      }));

      return duplicatedNode.id;
    },
    [workflow.nodes]
  );

  const getSelectedNode = useCallback(
    () => workflow.nodes.find((node) => node.id === selectedNodeId),
    [workflow.nodes, selectedNodeId]
  );

  const getWorkflowStats = useCallback(() => {
    const nodesByCategory = workflow.nodes.reduce((acc, node) => {
      const { category } = node.template;
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return {
      totalNodes: workflow.nodes.length,
      totalConnections: workflow.connections.length,
      nodesByCategory,
      hasValidationErrors: false,
    };
  }, [workflow]);

  return {
    workflow,
    selectedNodeId,
    isConfigModalOpen,
    isDragging,
    draggedNode,
    dragOffset,
    canvasOffset,
    zoom,
    connectionStart,
    tempConnection,
    addNode,
    removeNode,
    updateNodePosition,
    updateNodeParameters,
    addConnection,
    removeConnection,
    saveWorkflow,
    loadWorkflow,
    loadWorkflowFromTemplate,
    createNewWorkflow,
    duplicateNode,
    updateWorkflowState,
    deleteWorkflowById,
    deactivateWorkflow,
    setSelectedNodeId,
    setIsConfigModalOpen,
    setIsDragging,
    setDraggedNode,
    setDragOffset,
    setCanvasOffset,
    setZoom,
    setConnectionStart,
    setTempConnection,
    getSelectedNode,
    getWorkflowStats,
  };
};
